import { jsx, jsxs } from "react/jsx-runtime";
import { marked } from "marked";
import React__default from "react";
import { InlineCitation, InlineCitationText, InlineCitationCard, InlineCitationCardTrigger, InlineCitationCardBody, InlineCitationCarousel, InlineCitationCarouselHeader, InlineCitationCarouselPrev, InlineCitationCarouselNext, InlineCitationCarouselIndex, InlineCitationCarouselContent, InlineCitationCarouselItem, InlineCitationSource } from "../../ai-elements/inline-citation.js";
import { StoreLocatorWidget } from "../components/StoreLocatorWidget/index.js";
import { SurveyCarousel } from "../components/SurveyCarousel.js";
marked.use({
  gfm: true,
  breaks: true,
  pedantic: false
});
const extractCitationTags = (text) => {
  const citations = [];
  let citationIndex = 0;
  let currentPos = 0;
  let result = "";
  while (currentPos < text.length) {
    const citationStartIndex = text.indexOf("<citation", currentPos);
    if (citationStartIndex === -1) {
      result += text.substring(currentPos);
      break;
    }
    result += text.substring(currentPos, citationStartIndex);
    const tagEndIndex = text.indexOf(">", citationStartIndex);
    if (tagEndIndex === -1) {
      result += text.substring(citationStartIndex);
      break;
    }
    const openingTag = text.substring(citationStartIndex, tagEndIndex + 1);
    const trimmedTag = openingTag.trim();
    const isSelfClosing = trimmedTag.endsWith("/>") || trimmedTag.match(/\/\s*>$/);
    const sourceMatch = openingTag.match(/source\s*=\s*"([^"]+)"/);
    const typeMatch = openingTag.match(/type\s*=\s*"([^"]+)"/);
    const titleMatch = openingTag.match(/title\s*=\s*"([^"]+)"/);
    const source = sourceMatch ? sourceMatch[1] : "";
    const type = typeMatch ? typeMatch[1] : void 0;
    const title = titleMatch ? titleMatch[1] : void 0;
    if (isSelfClosing) {
      const placeholder2 = `@@CITATION_${citationIndex}@@`;
      citations.push({
        placeholder: placeholder2,
        source,
        type,
        title,
        text: title || source
        // Use title or source as the text for self-closing tags
      });
      result += placeholder2;
      citationIndex++;
      currentPos = tagEndIndex + 1;
      continue;
    }
    const closingTagIndex = text.indexOf("</citation>", tagEndIndex + 1);
    if (closingTagIndex === -1) {
      const placeholder2 = `@@CITATION_${citationIndex}@@`;
      citations.push({
        placeholder: placeholder2,
        source,
        type,
        title,
        text: title || source
        // Use title or source as the text
      });
      result += placeholder2;
      citationIndex++;
      currentPos = tagEndIndex + 1;
      continue;
    }
    const citationText = text.substring(tagEndIndex + 1, closingTagIndex);
    const placeholder = `@@CITATION_${citationIndex}@@`;
    citations.push({
      placeholder,
      source,
      type,
      title,
      text: citationText
    });
    result += placeholder;
    citationIndex++;
    currentPos = closingTagIndex + 11;
  }
  return {
    textWithPlaceholders: result,
    citations
  };
};
const renderMessageContentWithCarousels = (text, productMetadataMap, streamingGroups, isStreaming = false, isAlreadyProcessed = false, onVariantSelect, surveyMetadataMap) => {
  const { textWithPlaceholders: textWithCitations, citations } = extractCitationTags(text);
  const storeLocatorRegex = /<store_locator\s+stacklineSku\s*=\s*"([^"]+)"\s*\/?>/g;
  const storeLocators = [];
  let storeLocatorIndex = 0;
  const textWithStoreLocatorPlaceholders = textWithCitations.replace(
    storeLocatorRegex,
    (match, stacklineSku) => {
      const placeholder = `@@STORE_LOCATOR_${storeLocatorIndex}@@`;
      storeLocators.push({
        placeholder,
        stacklineSku
      });
      storeLocatorIndex++;
      return placeholder;
    }
  );
  const surveyGroupRegex = /(?:<survey\s+campaignId\s*=\s*"[^"]+"[^>]*>[\s\S]*?<\/survey>|<survey\s+campaignId\s*=\s*"[^"]+"\s*\/>)(?:\s*(?:<survey\s+campaignId\s*=\s*"[^"]+"[^>]*>[\s\S]*?<\/survey>|<survey\s+campaignId\s*=\s*"[^"]+"\s*\/>))*/g;
  const surveyGroups = [];
  let surveyGroupIndex = 0;
  const textWithSurveyPlaceholders = textWithStoreLocatorPlaceholders.replace(surveyGroupRegex, (groupMatch) => {
    const campaignIds = [];
    let match;
    const localRegex = /<survey\s+campaignId\s*=\s*"([^"]+)"[^>]*>[\s\S]*?<\/survey>|<survey\s+campaignId\s*=\s*"([^"]+)"\s*\/>/g;
    while ((match = localRegex.exec(groupMatch)) !== null) {
      campaignIds.push(match[1] || match[2]);
    }
    const placeholder = `@@SURVEY_GROUP_${surveyGroupIndex}@@`;
    surveyGroups.push({ placeholder, campaignIds });
    surveyGroupIndex++;
    return placeholder;
  });
  const htmlContent = marked.parse(textWithSurveyPlaceholders);
  const htmlContentWithoutHr = htmlContent.replace(/<hr\s*\/?>/gi, "");
  const htmlContentWithWrappedTables = htmlContentWithoutHr.replace(
    /<table[\s\S]*?<\/table>/gi,
    (match) => `<div class="table-scroll-wrapper">${match}</div>`
  );
  const parts = htmlContentWithWrappedTables.split(
    /(@@STORE_LOCATOR_\d+@@|@@SURVEY_GROUP_\d+@@|@@CITATION_\d+@@)/
  );
  return parts.map((part, idx) => {
    var _a, _b;
    const storeLocatorMatch = part.match(/@@STORE_LOCATOR_(\d+)@@/);
    if (storeLocatorMatch) {
      const storeLocatorIdx = parseInt(storeLocatorMatch[1]);
      const storeLocator = storeLocators[storeLocatorIdx];
      if (storeLocator) {
        return /* @__PURE__ */ jsx(
          StoreLocatorWidget,
          {
            stacklineSku: storeLocator.stacklineSku
          },
          `store-locator-${idx}-${storeLocator.stacklineSku}`
        );
      }
      return /* @__PURE__ */ jsx(React__default.Fragment, {}, `store-locator-missing-${idx}`);
    }
    const surveyGroupMatch = part.match(/@@SURVEY_GROUP_(\d+)@@/);
    if (surveyGroupMatch) {
      const groupIdx = parseInt(surveyGroupMatch[1]);
      const group = surveyGroups[groupIdx];
      if (group && surveyMetadataMap) {
        const surveysWithMetadata = [];
        for (const campaignId of group.campaignIds) {
          const surveyData = surveyMetadataMap.get(campaignId);
          if (surveyData) {
            surveysWithMetadata.push(surveyData);
          } else if (process.env.NODE_ENV === "development") {
            console.warn(
              `SurveyCarousel: Survey metadata not found for campaignId: ${campaignId}. Available campaignIds: ${Array.from(surveyMetadataMap.keys()).join(", ")}`
            );
          }
        }
        if (surveysWithMetadata.length > 0) {
          return /* @__PURE__ */ jsx(SurveyCarousel, { surveys: surveysWithMetadata }, `survey-group-${idx}`);
        }
      }
      return /* @__PURE__ */ jsx(React__default.Fragment, {}, `survey-group-empty-${idx}`);
    }
    const citationMatch = part.match(/@@CITATION_(\d+)@@/);
    if (citationMatch) {
      const citationIndex = parseInt(citationMatch[1]);
      const citation = citations[citationIndex];
      if (citation && citation.source) {
        const displayText = ((_a = citation.text) == null ? void 0 : _a.trim()) || citation.title || citation.source;
        const source = {
          title: citation.title || citation.source,
          url: citation.source,
          description: ((_b = citation.text) == null ? void 0 : _b.trim()) || void 0
        };
        return /* @__PURE__ */ jsxs(InlineCitation, { children: [
          /* @__PURE__ */ jsx(InlineCitationText, { url: citation.source, children: displayText }),
          /* @__PURE__ */ jsxs(InlineCitationCard, { children: [
            /* @__PURE__ */ jsx(InlineCitationCardTrigger, { sources: [citation.source] }),
            /* @__PURE__ */ jsx(InlineCitationCardBody, { children: /* @__PURE__ */ jsxs(InlineCitationCarousel, { children: [
              /* @__PURE__ */ jsxs(InlineCitationCarouselHeader, { children: [
                /* @__PURE__ */ jsx(InlineCitationCarouselPrev, {}),
                /* @__PURE__ */ jsx(InlineCitationCarouselNext, {}),
                /* @__PURE__ */ jsx(InlineCitationCarouselIndex, {})
              ] }),
              /* @__PURE__ */ jsx(InlineCitationCarouselContent, { children: /* @__PURE__ */ jsx(InlineCitationCarouselItem, { children: /* @__PURE__ */ jsx(InlineCitationSource, { description: source.description, title: source.title, url: source.url }) }) })
            ] }) })
          ] })
        ] }, `citation-${idx}`);
      }
      return /* @__PURE__ */ jsx(React__default.Fragment, {}, `citation-missing-${idx}`);
    }
    if (!part.trim()) {
      return /* @__PURE__ */ jsx(React__default.Fragment, {}, `empty-${idx}`);
    }
    return /* @__PURE__ */ jsx(
      "div",
      {
        style: { display: "flex", flexDirection: "column" },
        dangerouslySetInnerHTML: { __html: part },
        className: "message-markdown"
      },
      idx
    );
  });
};
export {
  extractCitationTags,
  renderMessageContentWithCarousels
};
//# sourceMappingURL=messageRenderer.js.map
